// apps/webapp/webapp/src/redesign/popover-two-column-table/popover-two-columns.tsx

import React from 'react';
import {
  Box,
  Divider,
  Fade,
  Grid,
  Popover,
  Stack,
  Typography,
  TypographyVariant,
} from '@mui/material';
import {
  ColoredIcon,
  ContentWrapper,
  HeaderContainer,
  IconWrapper,
  LabelContainer,
  PaperContainer,
} from './popover-two-columns-styled-components';

export type PopoverTwoColumnsInfoRow = {
  label: string;
  labelIcon?: React.ReactNode;
  labelVariant?: TypographyVariant;
  value: React.ReactNode;
};

export type PopoverTwoColumnsProps = {
  header: React.ReactNode;
  data: PopoverTwoColumnsInfoRow[][];
  anchorEl?: HTMLElement | null;
  maxWidth?: number | string;
  maxHeight?: number | string;
  onMouseLeave?: () => void;
  open?: boolean;
  onClose?: () => void;
};

export const PopoverTwoColumns = ({
  header,
  data,
  maxWidth,
  maxHeight,
  anchorEl,
  open,
  onClose,
}: PopoverTwoColumnsProps) => {
  const finalOpen = open !== undefined ? open : Boolean(anchorEl);

  return (
    <Popover
      open={finalOpen}
      sx={{ pointerEvents: 'none' }}
      anchorEl={anchorEl}
      onClose={onClose}
      disableEnforceFocus={true}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 0 }}
      disableRestoreFocus
      disableAutoFocus={true}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          width: maxWidth,
          maxHeight: maxHeight,
          overflow: 'hidden',
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(32, 45, 77, 0.12), 0 6px 12px rgba(32,45,77,0.06)',
          marginTop: 3,
        },
      }}
    >
      <Box>
        <PaperContainer>
          <HeaderContainer>{header}</HeaderContainer>

          <ContentWrapper>
            {data.map((groups, groupIndex) => (
              <Grid container spacing={3} key={'group-' + groupIndex}>
                {groups.map((row, idx) => (
                  <Grid key={idx} item xs={12}>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent={'space-between'}
                      alignItems="center"
                    >
                      <LabelContainer>
                        {row.labelIcon ? (
                          <IconWrapper>
                            <ColoredIcon>{row.labelIcon}</ColoredIcon>
                          </IconWrapper>
                        ) : null}

                        <Typography variant={row.labelVariant ?? 'body2'} fontWeight={600}>
                          {row.label}
                        </Typography>
                      </LabelContainer>
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          textAlign: 'right',
                          overflow: 'hidden',
                        }}
                      >
                        {typeof row.value === 'string' ? (
                          <Typography variant="body1" fontWeight={600} textOverflow={'ellipsis'}>
                            {row.value}
                          </Typography>
                        ) : (
                          row.value
                        )}
                      </Box>
                    </Stack>
                  </Grid>
                ))}
                {groupIndex < data.length - 1 && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
              </Grid>
            ))}
          </ContentWrapper>
        </PaperContainer>
      </Box>
    </Popover>
  );
};
